import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { logError } from "@/lib/log";

const driverIcon = (initial) =>
  L.divIcon({
    className: "",
    html: `<div style="background:#16A34A;color:#fff;width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-family:'JetBrains Mono',monospace;font-weight:700;font-size:14px;border:3px solid #fff;box-shadow:0 1px 6px rgba(0,0,0,.4)">${initial}</div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -16],
  });

function FitDrivers({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    if (points.length === 1) {
      map.setView(points[0], 12);
      return;
    }
    map.fitBounds(points, { padding: [50, 50] });
  }, [map, points]);
  return null;
}

const minsAgo = (iso) => Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));

/**
 * Shared live driver map: polls /drivers/locations every 30s.
 * Used inline by the admin Track driver page and inside the Dashboard dialog.
 */
export default function LiveDriverMap({ heightClass = "h-[60vh] sm:h-[65vh]" }) {
  const [locs, setLocs] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(() => {
    api.get("/drivers/locations")
      .then((r) => { setLocs(r.data || []); setLoaded(true); })
      .catch((e) => logError("Driver locations load failed:", e));
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const points = locs.map((d) => [Number(d.latitude), Number(d.longitude)]);

  return (
    <div>
      <p className="text-sm text-muted-foreground mb-3" data-testid="live-driver-map-status">
        {locs.length === 0
          ? "No drivers sharing GPS right now."
          : `${locs.length} driver${locs.length === 1 ? "" : "s"} live · refreshes every 30s`}
      </p>
      <div className={`${heightClass} w-full bg-secondary/40 border border-border rounded-sm overflow-hidden`}>
        {locs.length === 0 ? (
          <div className="h-full flex items-center justify-center text-sm text-muted-foreground text-center p-8">
            {loaded
              ? "Drivers appear here automatically while they have the Driver day page open on their phone (GPS permission required)."
              : "Loading…"}
          </div>
        ) : (
          <MapContainer
            key={`drivers-${locs.length}`}
            center={points[0]}
            zoom={11}
            style={{ height: "100%", width: "100%" }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {locs.map((d) => (
              <Marker key={d.user_id} position={[Number(d.latitude), Number(d.longitude)]}
                      icon={driverIcon((d.name || "?").charAt(0).toUpperCase())}>
                <Popup>
                  <div className="space-y-1">
                    <div className="font-bold text-sm">{d.name}</div>
                    <div className="text-xs text-muted-foreground">
                      Updated {minsAgo(d.updated_at)} min{minsAgo(d.updated_at) === 1 ? "" : "s"} ago
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}
            <FitDrivers points={points} />
          </MapContainer>
        )}
      </div>
    </div>
  );
}
